import React from 'react';
import { useTranslation } from 'localization';
import { Button } from 'primereact/button';
import './AccessDeniedMolecule.scss';

export interface AccessDeniedMoleculeProps {
  /** Override the heading. Defaults to `t('errors.accessDenied.title')`. */
  title?: string;
  /** Override the body message. Defaults to `t('errors.accessDenied.message')`. */
  message?: string;
  /** Render a retry button with this handler. When provided, a Retry button appears beside "Go home". */
  onRetry?: () => void;
  /** Handler for the "Go home" button. Defaults to navigating to `/`. */
  onBack?: () => void;
  /** Icon class to render above the title. Defaults to `pi pi-lock`. */
  iconClassName?: string;
  className?: string;
}

const AccessDeniedMolecule: React.FC<AccessDeniedMoleculeProps> = ({
  title,
  message,
  onRetry,
  onBack,
  iconClassName = 'pi pi-lock',
  className = '',
}) => {
  const { t } = useTranslation();

  const handleBack = onBack ?? (() => {
    if (typeof globalThis.window !== 'undefined') {
      globalThis.location.assign('/');
    }
  });

  const resolvedTitle = title ?? t('errors.accessDenied.title');
  const resolvedMessage = message ?? t('errors.accessDenied.message');

  return (
    <div
      className={`access-denied-molecule ${className}`.trim()}
      role="alert"
      aria-live="assertive"
    >
      <i
        className={`access-denied-molecule__icon ${iconClassName}`}
        aria-hidden="true"
      />
      <h1 className="access-denied-molecule__title">{resolvedTitle}</h1>
      <p className="access-denied-molecule__message">{resolvedMessage}</p>
      <div className="access-denied-molecule__actions">
        {onRetry && (
          <Button
            label={t('errors.accessDenied.retry')}
            icon="pi pi-refresh"
            onClick={onRetry}
            className="access-denied-molecule__retry-btn"
          />
        )}
        <Button
          label={t('errors.accessDenied.goHome')}
          icon="pi pi-home"
          onClick={handleBack}
          className="access-denied-molecule__back-btn"
        />
      </div>
    </div>
  );
};

export default AccessDeniedMolecule;
